'use client';

import { Tag } from '@zillow/constellation-10';
import type React from 'react';
import { CircleAlert } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { ShowMoreContent } from '@/src/components/features/property/ui/ShowMoreContent';
import { AmenitiesSpokeContent } from './AmenitiesSpoke';
import { highlightsContainer } from './amenitiesModule.styles';
import type { AmenityPreviewItem } from './amenitiesPreview';

type AmenitiesData = {
  amenities?: Array<unknown>;
  petPolicies?: {
    dog?: unknown;
    cat?: unknown;
  } | null;
};

type HdpT = ReturnType<typeof useTranslations<'hdp'>>;

const getPreviewDisplay = (item: AmenityPreviewItem, t: HdpT): { label: string; isNegative: boolean } => {
  if (item.kind === 'parking') {
    return {
      label: item.status === 'available' ? t('parking') : t('noParking'),
      isNegative: item.status === 'unavailable',
    };
  }
  if (item.kind === 'pets') {
    const label =
      item.status === 'friendly'
        ? t('petFriendly')
        : item.status === 'dogFriendly'
          ? t('dogFriendly')
          : item.status === 'catFriendly'
            ? t('catFriendly')
            : t('noPets');
    return { label, isNegative: item.status === 'unavailable' };
  }
  if (item.kind === 'laundry') {
    const label =
      item.status === 'inUnit'
        ? t('inUnitLaundryHighlight')
        : item.status === 'shared'
          ? t('sharedLaundryHighlight')
          : t('noLaundry');
    return { label, isNegative: item.status === 'unavailable' };
  }
  if (item.kind === 'airConditioning') return { label: t('airConditioning'), isNegative: false };
  if (item.kind === 'security') return { label: t('security'), isNegative: false };
  return { label: item.label, isNegative: false };
};

const getPreviewKey = (item: AmenityPreviewItem): string =>
  item.kind === 'other' ? `${item.kind}:${item.id}` : item.kind;

type Props = {
  listing: PropertyDetailListing;
};

export const AmenitiesModule: React.FC<Props> = ({ listing }) => {
  const t = useTranslations('hdp');

  const amenitiesData = listing?.amenities as AmenitiesData | undefined;
  const previewItems = listing.amenitiesPreview;

  if (previewItems.length === 0 && !amenitiesData) {
    return null;
  }
  const sectionLabel = t('amenitiesTitle');
  const hasAmenityDetails =
    (Array.isArray(amenitiesData?.amenities) && amenitiesData.amenities.length > 0) ||
    Boolean(listing.details?.leaseTerms) ||
    Boolean(amenitiesData?.petPolicies?.dog || amenitiesData?.petPolicies?.cat);

  if (previewItems.length === 0 && !hasAmenityDetails) {
    return null;
  }

  const amenityPills =
    previewItems.length > 0 ? (
      <div className={highlightsContainer}>
        {previewItems.map((item) => {
          const { label, isNegative } = getPreviewDisplay(item, t);
          return (
            <Tag
              key={getPreviewKey(item)}
              tone={isNegative ? 'critical' : 'success'}
              size="md"
              icon={isNegative ? <LucideIcon icon={CircleAlert} size="sm" color="white" /> : undefined}
            >
              {label}
            </Tag>
          );
        })}
      </div>
    ) : null;

  if (!hasAmenityDetails) {
    return amenityPills;
  }

  return (
    <ShowMoreContent
      controlsId="amenities-module-details"
      buttonTestId="button-show-more-what-you-get"
      preview={amenityPills}
      showMoreLabel={t('showMore')}
      showLessLabel={t('showLess')}
      showMoreAriaLabel={`${t('showMore')}: ${sectionLabel}`}
      showLessAriaLabel={`${t('showLess')}: ${sectionLabel}`}
    >
      <AmenitiesSpokeContent listing={listing} />
    </ShowMoreContent>
  );
};
